/**
 * Utility functions for optimizing image loading and display
 */

/**
 * Returns an object with props for optimized image loading
 * @param {string} src - Image source URL
 * @param {string} alt - Alternative text for the image
 * @param {string} className - CSS class name(s)
 * @returns {Object} - Props object for image element
 */
export const getOptimizedImageProps = (src, alt, className = '') => {
  return {
    src,
    alt: alt || 'Image',
    loading: 'lazy',
    className,
    onError: (e) => {
      console.error(`Failed to load image: ${src}`);
      e.target.onerror = null;
      e.target.src = '/images/placeholder.png'; // Fallback image
    }
  };
};

/**
 * Checks if an image URL is valid
 * @param {string} url - Image URL to check
 * @returns {Promise<boolean>} - Promise resolving to true if image is valid
 */
export const isImageValid = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

/**
 * Preloads critical images for faster rendering
 * @param {Array<string>} urls - Array of image URLs to preload
 */
export const preloadCriticalImages = (urls) => {
  if (!urls || !Array.isArray(urls)) return;
  
  urls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    document.head.appendChild(link);
  });
};

/**
 * Preloads a batch of images with progress tracking
 * @param {Array<string>} urls - Array of image URLs to preload
 * @param {Function} onProgress - Callback function for progress updates (receives percentage)
 * @param {Function} onComplete - Callback function when all images are loaded
 * @param {Function} onError - Callback function when an image fails to load
 * @returns {Promise<Array<HTMLImageElement>>} - Promise resolving to array of loaded images
 */
export const preloadImageBatch = (urls, onProgress, onComplete, onError) => {
  return new Promise((resolve) => {
    if (!urls || !Array.isArray(urls) || urls.length === 0) {
      if (onComplete) onComplete([]);
      resolve([]);
      return;
    }
    
    const images = [];
    let loadedCount = 0;
    const totalCount = urls.length;
    
    const checkIfAllLoaded = () => {
      const percent = Math.round((loadedCount / totalCount) * 100);
      if (onProgress) onProgress(percent);
      
      if (loadedCount === totalCount) {
        if (onComplete) onComplete(images);
        resolve(images);
      }
    };
    
    urls.forEach((url, index) => {
      const img = new Image();
      
      img.onload = () => {
        images[index] = img;
        loadedCount++;
        checkIfAllLoaded();
      };
      
      img.onerror = () => {
        console.error(`Failed to load image: ${url}`);
        if (onError) onError(url);
        loadedCount++;
        checkIfAllLoaded();
      };
      
      img.src = url;
    });
  });
};

/**
 * Optimizes image loading for a gallery
 * @param {Array<Object>} items - Array of items with image URLs
 * @param {string} imageProperty - Property name containing the image URL
 * @param {Function} onBatchProgress - Callback for batch progress
 * @returns {Promise<void>} - Promise that resolves when optimization is complete
 */
export const optimizeGalleryImageLoading = async (items, imageProperty = 'img', onBatchProgress) => {
  try {
    console.log('optimizeGalleryImageLoading: Starting with items:', items);
    
    if (!items || !Array.isArray(items) || items.length === 0) {
      console.log('optimizeGalleryImageLoading: No items to process, completing immediately');
      if (onBatchProgress) onBatchProgress(100); // Complete immediately if no items
      return;
    }
    
    // Extract image URLs
    const imageUrls = items.map(item => {
      if (!item) {
        console.warn('optimizeGalleryImageLoading: Encountered null or undefined item');
        return null;
      }
      
      if (!item[imageProperty]) {
        console.warn(`optimizeGalleryImageLoading: Missing ${imageProperty} property in item:`, item);
        return null;
      }
      
      return item[imageProperty];
    }).filter(Boolean);
    
    console.log('optimizeGalleryImageLoading: Extracted image URLs:', imageUrls);
    
    if (imageUrls.length === 0) {
      console.log('optimizeGalleryImageLoading: No valid URLs found, completing immediately');
      if (onBatchProgress) onBatchProgress(100); // Complete immediately if no valid URLs
      return;
    }
    
    // Group images into batches of 5 for progressive loading
    const batchSize = 5;
    const batches = [];
    
    for (let i = 0; i < imageUrls.length; i += batchSize) {
      batches.push(imageUrls.slice(i, i + batchSize));
    }
    
    console.log(`optimizeGalleryImageLoading: Created ${batches.length} batches of images`);
    
    // Load batches sequentially
    for (let i = 0; i < batches.length; i++) {
      const batchUrls = batches[i];
      console.log(`optimizeGalleryImageLoading: Processing batch ${i + 1}/${batches.length}:`, batchUrls);
      
      await preloadImageBatch(
        batchUrls,
        (progress) => {
          const overallProgress = Math.round(((i * batchSize) + (progress * batchSize / 100)) / imageUrls.length * 100);
          const clampedProgress = Math.min(overallProgress, 100);
          console.log(`optimizeGalleryImageLoading: Batch ${i + 1} progress: ${progress}%, overall: ${clampedProgress}%`);
          if (onBatchProgress) onBatchProgress(clampedProgress); // Ensure progress doesn't exceed 100%
        },
        (images) => {
          console.log(`optimizeGalleryImageLoading: Batch ${i + 1} complete, loaded ${images.length} images`);
        },
        (url) => console.error(`Failed to preload image: ${url}`)
      );
    }
    
    // Ensure we report 100% completion
    console.log('optimizeGalleryImageLoading: All batches processed, completing');
    if (onBatchProgress) onBatchProgress(100);
  } catch (error) {
    console.error('Error in optimizeGalleryImageLoading:', error);
    // Ensure we report 100% completion even on error to avoid UI being stuck in loading state
    if (onBatchProgress) onBatchProgress(100);
  }
};
